<script setup lang="ts">
import { computed } from 'vue'
import { getInvoiceStatusDisplay, type Invoice } from '@/modules/invoices/interfaces/invoice'

interface Props {
  invoice: Invoice | null
  canCreate?: boolean
  isActing?: boolean
  enabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  canCreate: true,
  isActing: false,
  enabled: true,
})

const emit = defineEmits<{
  (e: 'create'): void
  (e: 'view-draft'): void
  (e: 'view-stamped'): void
}>()

const statusDisplay = computed(() =>
  props.invoice ? getInvoiceStatusDisplay(props.invoice) : null,
)

const variantClass: Record<string, string> = {
  green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  red: 'bg-red-50 text-red-700 border-red-200',
  gray: 'bg-gray-100 text-gray-500 border-gray-200',
}
</script>

<template>
  <div class="border border-gray-200 rounded-lg p-3">
    <div class="flex items-center justify-between">
      <!-- Label + badge -->
      <div class="flex items-center gap-2">
        <span class="material-symbols-outlined text-[16px] text-gray-400">receipt_long</span>
        <span class="text-sm font-medium text-gray-700">Factura</span>

        <span
          v-if="statusDisplay"
          :class="[
            'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border',
            variantClass[statusDisplay.variant],
          ]"
        >
          {{ statusDisplay.label }}
        </span>
        <span v-else class="text-xs text-gray-400">Sin factura</span>
      </div>

      <!-- Acciones -->
      <div v-if="enabled">
        <!-- Sin factura o cancelada: crear borrador -->
        <button
          v-if="(!invoice || invoice.status === 'cancelada') && canCreate"
          type="button"
          :disabled="isActing"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#021F58] text-white hover:bg-[#1C3A7A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          @click="emit('create')"
        >
          <svg v-if="isActing" class="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span class="material-symbols-outlined text-[14px]" v-else>add</span>
          {{ invoice?.status === 'cancelada' ? 'Nueva factura' : 'Crear borrador' }}
        </button>

        <!-- Borrador: ver borrador -->
        <button
          v-else-if="invoice?.status === 'draft'"
          type="button"
          :disabled="isActing"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-[#021F58] text-[#021F58] hover:bg-[#021F58] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          @click="emit('view-draft')"
        >
          <span class="material-symbols-outlined text-[14px]">visibility</span>
          Ver borrador
        </button>

        <!-- Timbrada: ver factura -->
        <button
          v-else-if="invoice?.status === 'activa'"
          type="button"
          :disabled="isActing"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          @click="emit('view-stamped')"
        >
          <span class="material-symbols-outlined text-[14px]">receipt_long</span>
          Ver factura
        </button>
      </div>
    </div>
  </div>
</template>
